import { useEffect } from 'react';
import { useGameStore } from '../../store/gameStore';
import { toast } from 'react-hot-toast';
import React from 'react';
import CustomToast from '../subcomponents/Toast';
import { toZonedTime } from 'date-fns-tz';

export const getESTDateString = () => {
  const nowEST = toZonedTime(new Date(), 'America/New_York');
  const effectiveDate =
    nowEST.getHours() < 3
      ? new Date(nowEST.getFullYear(), nowEST.getMonth(), nowEST.getDate() - 1)
      : nowEST;

  const year = effectiveDate.getFullYear();
  const month = String(effectiveDate.getMonth() + 1).padStart(2, '0');
  const day = String(effectiveDate.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

const getMsUntilNextEST3am = () => {
  const nowEST = toZonedTime(new Date(), 'America/New_York');
  const isBefore3am = nowEST.getHours() < 3;
  const target = new Date(
    nowEST.getFullYear(),
    nowEST.getMonth(),
    nowEST.getDate() + (isBefore3am ? 0 : 1),
    3,
    0,
    0
  );
  return target.getTime() - nowEST.getTime();
};

const waitForHydratedState = async () => {
  return new Promise<void>((resolve) => {
    if (useGameStore.getState().hydrated) return resolve();
    const interval = setInterval(() => {
      if (useGameStore.getState().hydrated) {
        clearInterval(interval);
        resolve();
      }
    }, 50);
  });
};

const useGameReset = () => {
  useEffect(() => {
    const checkAndReset = async () => {
      const today = getESTDateString();
      const lastPlayed = useGameStore.getState().lastPlayedDate;

      if (!lastPlayed || lastPlayed !== today) {
        useGameStore.getState().setGameActive(false);

        setTimeout(() => {
          const state = useGameStore.getState();
          state.resetGame();
          state.setLastPlayedDate(today);
          state.setWasReset(true);
        }, 100);
      }
    };

    (async () => {
      await waitForHydratedState(); // explicitly wait for hydration

      await checkAndReset();

      const msUntilReset = getMsUntilNextEST3am();
      const resetTimeout = setTimeout(checkAndReset, msUntilReset);

      const warning = setTimeout(
        () => {
          toast.custom((t) =>
            React.createElement(CustomToast, {
              t,
              message: 'Game will reset in 1 minute',
              warning: true,
            })
          );
        },
        Math.max(msUntilReset - 60000, 0)
      );

      const interval = setInterval(checkAndReset, 60000);

      const resumeCheck = () => checkAndReset();

      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          resumeCheck();
        }
      });
      window.addEventListener('focus', resumeCheck);
      window.addEventListener('pageshow', (e) => {
        if (e.persisted || document.visibilityState === 'visible') {
          resumeCheck();
        }
      });

      return () => {
        clearTimeout(resetTimeout);
        clearTimeout(warning);
        clearInterval(interval);
        document.removeEventListener('visibilitychange', resumeCheck);
        window.removeEventListener('focus', resumeCheck);
        window.removeEventListener('pageshow', resumeCheck);
      };
    })();
  }, []);
};

export default useGameReset;
